import { FilterValue } from './filter-value';
import { RecordRequest } from './record-request';
import { SearchCriteria, SearchCriteriaField, SearchOrder, SearchTerm } from '../state/user/searches/models/search';
import { Record } from './record';
import { FromScreen } from './from-screen';
import { Util } from '../util';
import { convertToBoolean } from '../_core/utils/convert-to-boolean';

const AVAILABILITY_FILTER = 'availability_facet_ss';
const DEFAULT_ITEMS = 24;

const sortByConfiguration: SearchOrder[] = [
  {
    field: 'relevance',
    order: 'desc',
    key: 'search.order.relevance',
    accessibilityKey: 'sort.order.relevance',
  },
  {
    field: 'fechaModifica_dt',
    order: 'desc',
    key: 'search.order.date',
    accessibilityKey: 'sort.order.date',
  },
  {
    field: 'tituloExact_s',
    order: 'asc',
    key: 'search.order.titleAsc',
    accessibilityKey: 'sort.order.titleAsc',
  },
  {
    field: 'tituloExact_s',
    order: 'desc',
    key: 'search.order.titleDesc',
    accessibilityKey: 'sort.order.titleDesc',
  },
  {
    field: 'autorExact_s',
    order: 'asc',
    key: 'search.order.authorAsc',
    accessibilityKey: 'sort.order.authorAsc',
  },
  {
    field: 'autorExact_s',
    order: 'desc',
    key: 'search.order.authorDesc',
    accessibilityKey: 'sort.order.authorDesc',
  },
  {
    field: 'fechapublicacionSingle',
    order: 'desc',
    key: 'search.order.publicationYear',
    accessibilityKey: 'sort.order.publicationYear',
  },
];

const searchCriteria: SearchCriteria[] = [
  { field: SearchCriteriaField.ALL, key: 'search.criteria.all', icon: 'list' },
  { field: SearchCriteriaField.AUTHOR, key: 'search.criteria.author', icon: 'user' },
  { field: SearchCriteriaField.TITLE, key: 'search.criteria.title', icon: 'title' },
  { field: SearchCriteriaField.PUBLISHER, key: 'search.criteria.publisher', icon: 'publisher' },
  { field: SearchCriteriaField.SUBJECT, key: 'search.criteria.subject', icon: 'subject' },
  /**{ field: 'new_materia_facet_ss', key: 'search.criteria.subject', icon: 'list' },*/
];

export class SearchModel {
  public static readonly OR_KEYWORD = ' OR ';
  public static readonly AND_KEYWORD = ' AND ';

  limit = DEFAULT_ITEMS;
  offset = 0;
  terms: SearchTerm[] = [];
  defaultOrder = SearchModel.getDateSort();
  order: SearchOrder = this.defaultOrder;
  filters: FilterValue[] = [];
  collectionId: number;
  joinType = SearchModel.AND_KEYWORD;
  explore: boolean;
  excludeNonLeRecords: boolean;
  recordIds?: string[];
  showExperiences: boolean;
  fixedQuery = '';
  hideButtonsSideMenu: boolean;
  showReturnButton: boolean;
  title: string;
  originPage: string;
  careerPlanLevelId: string;
  challengeId: string;
  save = false;

  // TODO: Migrate SearchModel creation to a centralized store. Until then, every caller must pass
  // configService.get().resultsFilterOrder so the configured order applies when none is explicit.
  constructor(request?: RecordRequest, resultsFilterOrderFlag?: string) {
    this.applyResultsFilterOrder(resultsFilterOrderFlag);

    if (!request) {
      return;
    }

    this.limit = Number(request.limit) || DEFAULT_ITEMS;
    this.offset = Number(request.offset) || 0;

    if (request.facets) {
      this.filters = SearchModel.extractFilters(request.facets);
    }

    if (request.query) {
      this.setQueryTerms(request.query)
    }

    if (request.order) {
      const order = request.order.split(':');
      this.order = this.getOrderByField(order[0], order[1]) ?? this.defaultOrder;
    }

    this.excludeNonLeRecords = convertToBoolean(request.excludeNonLeRecords);
    this.collectionId = request.collectionId;
    this.explore = convertToBoolean(request.explore);
    this.showExperiences = convertToBoolean(request.showExperiences);
    this.hideButtonsSideMenu = convertToBoolean(request.hideButtonsSideMenu);
    this.showReturnButton = convertToBoolean(request.showReturnButton);
    this.title = request.title;
    this.originPage = request.originPage;
    this.careerPlanLevelId = request.careerPlanLevelId;
    this.challengeId = request.challengeId;
    this.save = request.save;

    if (request.recordIds) {
      this.recordIds = request.recordIds as string[];
    }
  }

  private setQueryTerms(query: string) {
    let fixedQuery = ''
    const userTermsQueryRegExpExec = new RegExp(/(allfields_txt:|author:|title:|publisher:|subject:|\d{3}\$.*:)/).exec(query)
    const userTermsQueryStartIndex = userTermsQueryRegExpExec?.index
    const hasUserTerms = userTermsQueryStartIndex != null
    const hasFixedQuery = hasUserTerms && userTermsQueryStartIndex > 0 || !hasUserTerms && query.length > 0
    if (hasFixedQuery) {
      if (hasUserTerms) {
        fixedQuery = query.substring(0, userTermsQueryStartIndex)
      } else {
        fixedQuery = query
      }
      if (fixedQuery.length >= SearchModel.AND_KEYWORD.length) {
        const fixedQueryLast5Chars = fixedQuery.substring(fixedQuery.length - SearchModel.AND_KEYWORD.length)
        this.fixedQuery = fixedQueryLast5Chars === SearchModel.AND_KEYWORD ? fixedQuery.substring(0, fixedQuery.length - SearchModel.AND_KEYWORD.length) : fixedQuery
      } else {
        this.fixedQuery = fixedQuery
      }
    }
    if (hasUserTerms) {
      const userTermsQuery = query.substring(userTermsQueryStartIndex);
      const groups = new RegExp(/(AND|OR)/).exec(userTermsQuery);
      if (groups && groups.length) {
        if (SearchModel.OR_KEYWORD.includes(groups[0])) {
          this.joinType = SearchModel.OR_KEYWORD;
        }
      }

      userTermsQuery.split(this.joinType).forEach((term) => this.addRawTerm(term));
    }

    this.defaultOrder = SearchModel.getRelevanceSort();
  }
  removeFixedQuery() {
    this.fixedQuery = '';
  }

  private static getDateSort(): SearchOrder {
    return sortByConfiguration.find((s) => s.field === 'fechaModifica_dt');
  }

  private static getRelevanceSort(): SearchOrder {
    return sortByConfiguration.find((s) => s.field === 'relevance');
  }

  private static removeQuotes(value: string): string {
    return value.replace(/"/g, '');
  }

  private static extractFilters(facets: string): FilterValue[] {
    const result = facets.match(Util.QUOTED_TERMS_PATTERN);

    return result.map((r) => {
      const parts = Util.split(r, ':', 1, ':');
      return {
        name: parts[0].replace(';', ''),
        value: SearchModel.removeQuotes(parts[1]),
      };
    });
  }

  public addRawTerm(term: string) {
    const parts = Util.split(term, ':', 1);
    this.addTerm({
      field: parts[0],
      term: parts[1],
      icon: this.getTermIcon(parts[0]),
    });
  }

  public addTerm(term: SearchTerm) {
    this.terms.push(term);
  }

  public removeTerm(term: SearchTerm) {
    this.terms = this.terms.filter((_term) => _term !== term);
  }

  public applyFilter(filter: FilterValue) {
    if (this.hasFilter(filter)) {
      if (filter.single) {
        this.filters = this.filterByName(filter.name);
      } else {
        this.filters = this.filterByValue(filter.value);
      }
    } else {
      if (filter.single) {
        this.filters = this.filterByName(filter.name);
      }

      this.filters.push(filter);
    }

    const availability = this.filters.find((f) => f.name === AVAILABILITY_FILTER);

    if (availability) {
      const tempFilters: FilterValue[] = this.filters;
      // move availability facet to last position
      tempFilters.push(tempFilters.splice(tempFilters.indexOf(availability), 1)[0]);
      this.filters = tempFilters;
    }

    // reset pagination
    this.offset = 0;
  }

  public hasFilter(filter: FilterValue): boolean {
    if (filter.single) {
      return this.filters.some((_filter) => _filter.name === filter.name);
    } else {
      return this.filters.some((_filter) => _filter.name === filter.name && _filter.value === filter.value);
    }
  }

  public hasFilters(): boolean {
    return this.filters.length > 0;
  }

  public hasTerms(): boolean {
    return this.terms.length > 0;
  }

  public hasCollectionId(): boolean {
    return this.collectionId !== undefined;
  }

  private completeBaseRequest(request: RecordRequest, state?: SearchState) {
    if (this.hasFilters()) {
      request.facets = this.getFacets();
    }

    if (this.hasQuery()) {
      const andKeywordOrEmpty = this.fixedQuery && this.hasTerms() ? SearchModel.AND_KEYWORD : '';
      request.query = this.fixedQuery + andKeywordOrEmpty + this.terms.map((term) => `${term.field}:${term.term}`).join(this.joinType);
    }

    if (this.hasCollectionId()) {
      request.collectionId = this.collectionId;
    }

    if (this.explore && !this.hasTerms() && !this.hasFilters()) {
      request.explore = true;
    }

    this.order = this.determineOrder();
    if (this.order) {
      request.order = `${this.order.field}:${this.order.order}`;
    }

    if (this.recordIds && state) {
      request.recordIds = this.recordIds.slice(this.offset, this.offset + this.limit);
    }

    if (this.save) {
      request.save = this.save;
    }

    if (state) {
      if (convertToBoolean(state.searchModel?.excludeNonLeRecords)) {
        request.excludeNonLeRecords = convertToBoolean(state.searchModel?.excludeNonLeRecords);
      }
      if (convertToBoolean(state.searchModel?.hideButtonsSideMenu)) {
        request.hideButtonsSideMenu = convertToBoolean(state.searchModel?.hideButtonsSideMenu);
      }
      if (convertToBoolean(state.searchModel?.showReturnButton)) {
        request.showReturnButton = convertToBoolean(state.searchModel?.showReturnButton);
      }
      if (state.searchModel?.title) {
        request.title = state.searchModel?.title;
      }
      if (state.searchModel?.originPage) {
        request.originPage = state.searchModel?.originPage;
      }
      if (state.searchModel?.careerPlanLevelId) {
        request.careerPlanLevelId = state.searchModel?.careerPlanLevelId;
      }
      if (state.searchModel?.challengeId) {
        request.challengeId = state.searchModel?.challengeId;
      }
    }

    return request;
  }

  public getFacets() {
    return this.filters.map((filter) => `${filter.name}:"${filter.value}"`).join(';');
  }

  public getQuery() {
    return this.terms.map((term) => `${term.field}:${term.term}`).join(this.joinType);
  }

  public hasQuery() {
    return this.hasTerms() || this.fixedQuery
  }

  public toRequest(state?: SearchState): RecordRequest {
    const baseRequest: RecordRequest = {
      limit: this.limit,
      offset: this.offset,
      recordIds: this.recordIds,
    };

    return this.completeBaseRequest(baseRequest, state);
  }

  public toRequestLearningExperiences(): RecordRequest {
    const baseRequest: RecordRequest = {
      limit: this.limit,
      offset: this.offset,
      showExperiences: true,
      excludeNonLeRecords: true,
    };

    return this.completeBaseRequest(baseRequest);
  }

  public toYoutubeQuery(): string {
    if (this.hasTerms()) {
      return this.terms.flatMap((term) => term.term.split(' ')).join(',');
    }
    return '';
  }

  public reset(resultsFilterOrderFlag?: string) {
    this.limit = DEFAULT_ITEMS;
    this.offset = 0;
    this.filters = [];
    this.terms = [];
    this.order = this.defaultOrder;
    this.collectionId = undefined;
    this.applyResultsFilterOrder(resultsFilterOrderFlag);
  }

  public getSortOptions(): SearchOrder[] {
    return sortByConfiguration;
  }

  public determineOrder(): SearchOrder {
    if (this.order !== this.defaultOrder) {
      return this.order;
    }

    return !this.terms.length ? SearchModel.getDateSort() : SearchModel.getRelevanceSort();
  }

  public getSearchCriterias(): SearchCriteria[] {
    return searchCriteria;
  }

  public getTermIcon(field: string): string {
    const criteria = searchCriteria.find((_criteria) => _criteria.field === field);
    return criteria && criteria.field !== Util.ALLFIELDS ? criteria.icon : null;
  }

  private applyResultsFilterOrder(resultsFilterOrderFlag?: string) {
    if (resultsFilterOrderFlag) {
      this.order = this.getOrderByField(resultsFilterOrderFlag.split(':')[0], resultsFilterOrderFlag.split(':')[1]) ?? this.defaultOrder;
    }
  }

  private getOrderByField(field: string, order: string): SearchOrder {
    return sortByConfiguration.find((sort) => sort.field === field && sort.order === order);
  }

  private filterByName(name: string): FilterValue[] {
    return this.filters.filter((f) => f.name !== name);
  }

  private filterByValue(value: string | number): FilterValue[] {
    return this.filters.filter((f) => f.value !== value);
  }
}

export class SearchState {
  index: number;
  page: number;
  pageSize: number;
  records: Record[];
  total: number;
  genesis: SearchModel;
  searchModel: SearchModel;
  from: FromScreen;
  listSlug?: string;
  prevState?: SearchState;
  prevNav?: string;
}
